import React, { useState, useEffect } from 'react';
import { FiBell, FiX } from 'react-icons/fi';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './PushNotifications.css';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
};

export function usePushNotifications() {
  const [permission, setPermission] = useState(Notification?.permission || 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    const perm = Notification.permission;
    setPermission(perm);
    if (perm === 'granted') {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch {}
    }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { toast.error('Notification permission denied'); return; }
      const reg = await navigator.serviceWorker.ready;
      const res = await api.get('/push/vapid-public-key');
      if (!res.data?.publicKey) throw new Error('VAPID key missing');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(res.data.publicKey)
      });
      await api.post('/push/subscribe', { subscription: sub });
      setSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (err) {
      toast.error('Failed to enable notifications: ' + (err.message || 'Unknown error'));
    } finally { setLoading(false); }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
      }
      setSubscribed(false);
      toast.success('Push notifications disabled');
    } catch { toast.error('Failed to disable notifications'); }
    finally { setLoading(false); }
  };

  return { permission, subscribed, loading, subscribe, unsubscribe };
}

function PushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { checkNotificationStatus(); }, []);

  const checkNotificationStatus = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    const perm = Notification.permission;
    setPermission(perm);
    if (perm === 'granted') {
      // Permission is already granted (e.g. in the installed Mac app), so make
      // sure this device really has a push subscription AND the server knows
      // about it. The server drops expired subscriptions, and the installed
      // app never showed the prompt again, so pushes silently stopped.
      try {
        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          const res = await api.get('/push/vapid-public-key');
          if (res.data?.publicKey) {
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(res.data.publicKey)
            });
          }
        }
        if (sub) await api.post('/push/subscribe', { subscription: sub });
        setSubscribed(!!sub);
      } catch (err) {
        console.error('[PUSH] Could not refresh subscription:', err);
      }
    } else if (perm === 'default') {
      setTimeout(() => setShowPrompt(true), 3000);
    }
  };

  const subscribeToPush = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { toast.error('Notification permission denied'); return; }
      const reg = await navigator.serviceWorker.ready;
      const res = await api.get('/push/vapid-public-key');
      if (!res.data?.publicKey) throw new Error('VAPID key missing');
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(res.data.publicKey) });
      await api.post('/push/subscribe', { subscription: sub });
      setSubscribed(true);
      setShowPrompt(false);
      toast.success('Push notifications enabled!');
    } catch (err) {
      toast.error('Failed to enable notifications: ' + (err.message || 'Unknown error'));
    } finally { setLoading(false); }
  };

  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;

  return (
    <>
      {showPrompt && permission === 'default' && !subscribed && (
        <div className="notification-prompt">
          <div className="notification-prompt-content">
            <button className="close-prompt-btn" onClick={() => setShowPrompt(false)}><FiX /></button>
            <div className="prompt-icon"><FiBell size={40} /></div>
            <h3>Stay Updated!</h3>
            <p>Enable notifications to receive instant alerts for messages and calls, even when the app is closed.</p>
            <div className="prompt-actions">
              <button className="btn-secondary" onClick={() => setShowPrompt(false)}>Not Now</button>
              <button className="btn-primary" onClick={subscribeToPush} disabled={loading}>
                {loading ? 'Enabling...' : 'Enable Notifications'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PushNotifications;
